import React from "react";
import { useTranslation } from "react-i18next";
import { Switch } from "../../../../../components/ui/Switch";
import { capabilitiesForAudience } from "../agentPayload";
import { isGrantable } from "../aiErrorMessage";
import { MetaBadge, SectionTitle } from "./primitives";

// Capacidades del agente: qué puede hacer además de conversar, filtradas por
// la audiencia elegida en Identidad. Las de escritura piden confirmación en el
// chat (el cliente ve el cambio antes de que ocurra), y eso se dice acá.

function CapabilityRow({ capability, checked, onToggle }) {
  const { t } = useTranslation();
  const isWrite = capability.kind === "write";
  const helpId = `agent-cap-${capability.id}-help`;
  // El rol del llamador no puede conceder esta capacidad: el switch queda
  // deshabilitado y con su explicación a la vista. Si el agente YA la tenía,
  // sigue marcada y también deshabilitada — quitarla sería reescribir una
  // configuración que este rol no administra (y el backend lo rechazaría).
  const notGrantable = !isGrantable(capability);
  const grantId = `${helpId}-grant`;

  return (
    <li className="flex items-start justify-between gap-4 py-3 first:pt-0 last:pb-0">
      <span className="min-w-0">
        <span className="flex items-center gap-1.5 flex-wrap">
          <span className="text-xs font-medium text-fg">{capability.label}</span>
          {isWrite && <MetaBadge tone="brand">{t("pide confirmación en el chat")}</MetaBadge>}
        </span>
        {capability.description && (
          <span id={helpId} className="block text-[12px] md:text-[11px] text-fg-muted">
            {capability.description}
          </span>
        )}
        {notGrantable && (
          <span id={grantId} className="block text-[12px] md:text-[11px] text-fg-muted">
            {t("Tu rol no permite conceder esta capacidad.")}
          </span>
        )}
      </span>
      <Switch
        size="sm"
        checked={checked}
        onChange={onToggle}
        disabled={notGrantable}
        ariaLabel={capability.label}
        aria-describedby={
          [capability.description ? helpId : null, notGrantable ? grantId : null]
            .filter(Boolean)
            .join(" ") || undefined
        }
      />
    </li>
  );
}

export default function CapabilitiesSection({ value, audience, catalog, onChange }) {
  const { t } = useTranslation();
  const available = capabilitiesForAudience(catalog, audience);
  const selected = {
    read: Array.isArray(value?.read) ? value.read : [],
    write: Array.isArray(value?.write) ? value.write : [],
  };

  const toggle = (capability) => {
    const kind = capability.kind === "write" ? "write" : "read";
    const ids = selected[kind];
    const next = ids.includes(capability.id)
      ? ids.filter((id) => id !== capability.id)
      : [...ids, capability.id];
    onChange({ [kind]: next });
  };

  const total = available.read.length + available.write.length;

  return (
    <section className="space-y-3">
      <SectionTitle>{t("Capacidades")}</SectionTitle>
      <p className="text-xs text-fg-muted">
        {t("Qué puede hacer este agente además de conversar. Solo se ofrecen las que aplican a su audiencia.")}
      </p>

      {total === 0 ? (
        <p className="text-[12px] md:text-[11px] text-fg-muted">
          {t("No hay capacidades disponibles para esta audiencia.")}
        </p>
      ) : (
        <div className="space-y-4">
          {[
            { kind: "read", title: t("Solo lectura"), items: available.read },
            { kind: "write", title: t("Puede modificar"), items: available.write },
          ]
            .filter((group) => group.items.length > 0)
            .map((group) => (
              <div key={group.kind} className="space-y-1">
                <p className="text-xs font-medium text-fg">{group.title}</p>
                <ul className="divide-y divide-border">
                  {group.items.map((capability) => (
                    <CapabilityRow
                      key={capability.id}
                      capability={capability}
                      checked={selected[group.kind].includes(capability.id)}
                      onToggle={() => toggle(capability)}
                    />
                  ))}
                </ul>
              </div>
            ))}
        </div>
      )}
    </section>
  );
}
